import { useState } from 'react';
import { Box, Button, Grid, Skeleton, Stack, useMediaQuery } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiErrorState } from '@/components/ApiErrorState';
import { PageHeader } from '@/components/PageHeader';
import { useToast } from '@/components/useToast';
import { stringParam, useUrlState } from '@/components/useUrlState';
import { useCanWrite } from '@/auth/authHooks';
import { useTenantApi, useTenantCurrency, useTenantLink } from '@/tenant/useTenant';
import { AffiliateDetail } from './AffiliateDetail';
import { AffiliateList } from './AffiliateList';
import { AffiliateSettingsDialog } from './AffiliateSettingsDialog';
import { InviteAffiliateDialog } from './InviteAffiliateDialog';
import { useAffiliate } from './useAffiliate';
import { useAffiliates } from './useAffiliates';

const SCHEMA = { q: stringParam() };

export function AffiliatesScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const selectedId = params.id !== undefined ? Number(params.id) : undefined;
  const id = selectedId !== undefined && Number.isFinite(selectedId) ? selectedId : undefined;
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const currency = useTenantCurrency();
  const showToast = useToast();
  const api = useTenantApi();
  const link = useTenantLink();
  const canWrite = useCanWrite();
  const list = useAffiliates();
  const detail = useAffiliate(id);
  // The search in the URL (`?q=`, TMM-B6); the list narrows by the typed text at once.
  const url = useUrlState(SCHEMA, { search: 'q' });
  const [dialog, setDialog] = useState<'invite' | 'settings' | null>(null);

  const actions = (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
      <Button onClick={() => setDialog('invite')}>{t('affiliates.invite')}</Button>
      <Button onClick={() => setDialog('settings')}>{t('affiliates.settings')}</Button>
    </Stack>
  );

  // After a list action, the open detail is refreshed quietly — and only when it's the affiliate
  // acted on (TMMRSL-0053); a failed refresh keeps what's on screen.
  function refreshDetailOf(affiliateId: number) {
    if (id === affiliateId) detail.refresh().catch(() => undefined);
  }

  const showDetail = id !== undefined;
  const showList = mdUp || !showDetail;
  // Close and the mobile Back return to the list with its query (TMM-B6).
  const toList = () => navigate({ pathname: link('affiliates'), search: location.search });
  // "Set up deal" (the list) opens the detail with `state.focusDeal`; the deal editor clears it.
  // Only once the detail shown is this affiliate's — not the previous one's, still on screen.
  const focusDeal =
    (location.state as { focusDeal?: unknown } | null)?.focusDeal === true &&
    detail.status === 'ready' &&
    detail.data?.detail.id === id;

  return (
    <Box>
      <PageHeader id="affiliates" actions={mdUp && canWrite ? actions : undefined} />
      {list.status === 'loading' && !list.data ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={280} />
        </Box>
      ) : null}
      {list.status === 'error' ? <ApiErrorState what={t('errors.what.affiliates')} error={list.error} onRetry={list.reload} /> : null}
      {list.data ? (
        <Grid container spacing={2}>
          {showList ? (
            <Grid size={{ xs: 12, md: 5 }}>
              <AffiliateList
                affiliates={list.data.list.affiliates ?? []}
                query={url.searchText}
                selectedId={id}
                onQuery={url.setSearchText}
                onInvite={() => setDialog('invite')}
                onSettings={() => setDialog('settings')}
                onApprove={async (affiliateId) => {
                  await api.approveAffiliate(affiliateId);
                  list.reload();
                  refreshDetailOf(affiliateId);
                }}
                onDecline={async (affiliateId) => {
                  await api.endAffiliate(affiliateId);
                  list.reload();
                  refreshDetailOf(affiliateId);
                }}
                onReload={(affiliateId) => {
                  list.reload();
                  refreshDetailOf(affiliateId);
                }}
                showToolbarActions={!mdUp && canWrite}
              />
            </Grid>
          ) : null}
          {showDetail ? (
            <Grid size={{ xs: 12, md: 7 }}>
              {!mdUp ? (
                <Button onClick={toList} sx={{ mb: 1 }}>
                  {t('affiliates.back')}
                </Button>
              ) : null}
              <AffiliateDetail
                key={id}
                state={detail}
                currency={currency}
                onClose={toList}
                onListReload={() => {
                  list.reload();
                }}
                focusDeal={focusDeal}
              />
            </Grid>
          ) : null}
        </Grid>
      ) : null}
      <InviteAffiliateDialog
        open={dialog === 'invite'}
        onClose={() => setDialog(null)}
        onInvite={async (body) => {
          await list.invite(body);
          showToast(t('affiliates.toast.invited', { name: body.displayName }));
        }}
      />
      <AffiliateSettingsDialog
        open={dialog === 'settings'}
        settings={list.data?.settings ?? null}
        settingsError={list.settings.status === 'error' ? list.settings.error : null}
        onRetrySettings={list.settings.reload}
        settingsLoading={list.settings.status === 'loading'}
        onClose={() => setDialog(null)}
        onSave={async (body) => {
          await list.updateSettings(body);
          showToast(t('affiliates.toast.settingsSaved'));
        }}
      />
    </Box>
  );
}
